import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

export interface UseContentBriefShadowPopoverStateProps {
  blockIndex: number | null
  comment: string | undefined
  isOpen: boolean
  onCommentChange: (blockIndex: number, comment: string | undefined) => void
  onClose: () => void
}

export interface UseContentBriefShadowPopoverStateReturn {
  draftComment: string
  setDraftComment: (value: string) => void
  textareaRef: RefObject<HTMLTextAreaElement | null>
  handleSave: () => void
  handleCancel: () => void
  handleDelete: () => void
}

export const useContentBriefShadowPopoverState = ({
  blockIndex,
  comment,
  isOpen,
  onCommentChange,
  onClose,
}: UseContentBriefShadowPopoverStateProps): UseContentBriefShadowPopoverStateReturn => {
  const [draftComment, setDraftComment] = useState(comment ?? '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (isOpen) {
      setDraftComment(comment ?? '')
      requestAnimationFrame(() => textareaRef.current?.focus())
    }
  }, [isOpen, comment])

  const handleSave = useCallback((): void => {
    if (blockIndex === null) return
    const trimmed = draftComment.trim()
    onCommentChange(blockIndex, trimmed || undefined)
    onClose()
  }, [blockIndex, draftComment, onCommentChange, onClose])

  const handleCancel = useCallback((): void => {
    setDraftComment(comment ?? '')
    onClose()
  }, [comment, onClose])

  const handleDelete = useCallback((): void => {
    if (blockIndex === null) return
    onCommentChange(blockIndex, undefined)
    setDraftComment('')
    onClose()
  }, [blockIndex, onCommentChange, onClose])

  return { draftComment, setDraftComment, textareaRef, handleSave, handleCancel, handleDelete }
}
